import { z } from "zod";
import { useMemo } from "react";
import { useMutation, useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";

export const CohortDefinitionSourceSchema = z.enum(["builtin", "json"]);

export const CohortDefinitionSchema = z.object({
    id: z.number(),
    code: z.string(),
    label: z.string(),
    entityClass: z.enum(["Organization", "Product", "Ingredient", "Packaging"]),
    source: CohortDefinitionSourceSchema,
    attributeKey: z.string(),
    minimumPeerCount: z.number(),
});

export type CohortDefinition = z.infer<typeof CohortDefinitionSchema>;

export type CohortDefinitionWrite = Omit<CohortDefinition, "id"> & { id?: number };

const CohortAttributeChoiceSchema = z.object({
    key: z.string(),
    label: z.string(),
    source: z.string(),
});

export type CohortAttributeChoice = z.infer<typeof CohortAttributeChoiceSchema>;

export const useGetAllCohortDefinitions = () => {
    const url = "/api/cohort-definitions";

    const query = useQuery({
        queryKey: ["getAllCohortDefinitions"],
        staleTime: 5 * 60 * 1000,
        queryFn: async () => loggedInApiCall.get(url),
        select: (response) => {
            const members = response.data["hydra:member"];
            return zodParseWithLog(members, CohortDefinitionSchema.array(), "getAllCohortDefinitions");
        },
    });

    const cohortDefinitions = useMemo(() => query.data ?? [], [query.data]);

    return { cohortDefinitions, ...query };
};

export const useCohortAttributeChoices = (entityClass: string | undefined) => {
    const query = useQuery({
        queryKey: ["cohortAttributeChoices", entityClass],
        enabled: Boolean(entityClass),
        staleTime: 5 * 60 * 1000,
        queryFn: async () => {
            const url = `/api/cohort-definitions/attribute-choices?entityClass=${encodeURIComponent(entityClass ?? "")}`;
            return loggedInApiCall.get(url);
        },
        select: (response) => {
            const members = response.data["hydra:member"];
            return zodParseWithLog(members, CohortAttributeChoiceSchema.array(), "cohortAttributeChoices");
        },
    });

    return {
        attributeChoices: query.data ?? [],
        ...query,
    };
};

export const useCreateOrUpdateCohortDefinition = () => {
    return useMutation({
        mutationFn: async (definition: CohortDefinitionWrite) => {
            if (definition.id === undefined) {
                return loggedInApiCall.post("/api/cohort-definitions", definition);
            }

            const { id, ...body } = definition;
            return loggedInApiCall.put(`/api/cohort-definitions/${id}`, body);
        },
    });
};

export const useDeleteCohortDefinition = () => {
    return useMutation({
        mutationFn: async (id: number) => loggedInApiCall.delete(`/api/cohort-definitions/${id}`),
    });
};
